import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { Typography } from '../../atoms';

export interface ScrollerOption {
    id: string;
    title: string;
    description?: string;
    [key: string]: any; // Allow additional properties
}

export interface VerticalScrollerProps {
    options: ScrollerOption[];
    selectedId?: string;
    // eslint-disable-next-line
    onSelectionChange: (selectedId: string) => void;
    className?: string;
    trackHeight?: number;
    indicatorSize?: number;
    optionClassName?: string;
    selectedOptionClassName?: string;
    trackClassName?: string;
    indicatorClassName?: string;
    // eslint-disable-next-line
    renderOption?: (option: ScrollerOption, isSelected: boolean) => React.ReactNode;
}

const VerticalScroller: React.FC<VerticalScrollerProps> = ({
    options,
    selectedId,
    onSelectionChange,
    className = '',
    trackHeight = 250,
    indicatorSize = 45,
    optionClassName = '',
    selectedOptionClassName = '',
    trackClassName = '',
    indicatorClassName = '',
    renderOption
}) => {
    const theme = useTheme();
    const [internalSelectedId, setInternalSelectedId] = useState<string>(
        selectedId || (options.length > 0 ? options[0].id : '')
    );
    const [isDragging, setIsDragging] = useState<boolean>(false);

    // Use internal state if no selectedId prop is provided (uncontrolled)
    const currentSelectedId = selectedId !== undefined ? selectedId : internalSelectedId;

    const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const percentage = y / trackHeight;
        const index = Math.round(percentage * (options.length - 1));
        const clampedIndex = Math.max(0, Math.min(index, options.length - 1));

        const newSelectedId = options[clampedIndex].id;
        setInternalSelectedId(newSelectedId);
        onSelectionChange(newSelectedId);
    };

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
        e.preventDefault();
        const currentIndex = options.findIndex((option) => option.id === currentSelectedId);
        let newIndex: number;

        if (e.deltaY > 0) {
            newIndex = Math.min(currentIndex + 1, options.length - 1);
        } else {
            newIndex = Math.max(currentIndex - 1, 0);
        }

        const newSelectedId = options[newIndex].id;
        setInternalSelectedId(newSelectedId);
        onSelectionChange(newSelectedId);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent): void => {
        if (!isDragging) return;

        const scrollerContainer = document.querySelector('.go-scroller-container') as HTMLElement;
        if (!scrollerContainer) return;

        const rect = scrollerContainer.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const clampedY = Math.max(0, Math.min(y, trackHeight));
        const percentage = clampedY / trackHeight;
        const index = Math.round(percentage * (options.length - 1));

        if (options[index]) {
            const newSelectedId = options[index].id;
            setInternalSelectedId(newSelectedId);
            onSelectionChange(newSelectedId);
        }
    };

    const handleMouseUp = (): void => {
        setIsDragging(false);
    };

    const handleOptionClick = (optionId: string): void => {
        setInternalSelectedId(optionId);
        onSelectionChange(optionId);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);

    const selectedIndex = options.findIndex((option) => option.id === currentSelectedId);
    const indicatorPosition =
        options.length > 1 ? (selectedIndex / (options.length - 1)) * (trackHeight - (indicatorSize + (options.length > 3 ? 20: 0))) : 0;

    const defaultRenderOption = (option: ScrollerOption, isSelected: boolean) => (
        <div className={`go-scroll-option-content ${isSelected ? 'go-scroll-option-selected' : ''}`}>
            <Typography
                label={option.title}
                color={isSelected ? 'semantic.int.link' : 'semantic.text.default'}
                variant="labels.largeDemibold"
                sx={{ fontWeight: 700, marginBottom: 4 }}
            />
            {option.description && (
                <Typography
                    label={option.description}
                    color={isSelected ? 'semantic.int.link' : 'semantic.text.default'}
                    variant="labels.small"
                    sx={{ fontWeight: 400 }}
                />
            )}
        </div>
    );

    return (
        <div className={`go-scroll-vertical-scroller ${className}`}>
            <div className="go-scroll-main-content">
                <div className="go-scroller-container">
                    <div
                        className={`go-scroll-scroller-track-new ${trackClassName}`}
                        onClick={handleTrackClick}
                        onWheel={handleWheel}
                        style={{ height: `${trackHeight - 15}px` }}
                    >
                        {options.map((option, index) => (
                            <div
                                key={`track-${option.id}`}
                                className="go-scroll-track-area"
                                style={{
                                    top: `${(index / (options.length - 1)) * (trackHeight - 60)}px`
                                }}
                            />
                        ))}
                    </div>
                    <div
                        className={`go-scroll-scroller-indicator-new ${indicatorClassName}`}
                        style={{
                            top: `${indicatorPosition}px`,
                            backgroundColor: theme.palette.semantic.text.info,
                            left: '-10px'
                        }}
                        onMouseDown={handleMouseDown}
                        onWheel={handleWheel}
                    />
                </div>
                {/* Options */}
                <div className={`go-scroll-risk-options ${options.length > 3 ? 'new' : ''}`}>
                    {options.map((option) => (
                        <div
                            key={option.id}
                            onClick={() => handleOptionClick(option.id)}
                            className={`go-scroll-risk-option ${optionClassName} ${
                                currentSelectedId === option.id
                                    ? `go-scroll-option-selected ${selectedOptionClassName}`
                                    : ''
                            }`}
                        >
                            {renderOption
                                ? renderOption(option, currentSelectedId === option.id)
                                : defaultRenderOption(option, currentSelectedId === option.id)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VerticalScroller;
